import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { CMSService } from "../../../../services/cms";

interface ServicesTabProps {
  services: CMSService[];
  onEdit: (service?: CMSService) => void;
  onDelete: (id: string) => void;
}

const ServicesTab: React.FC<ServicesTabProps> = ({
  services,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.services")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #ff0080 0%, #cc0066 100%)",
            boxShadow: "0 2px 8px rgba(255,0,128,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addService")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(250px, 1fr))",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {services.map((service) => (
          <Card key={service.id} sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${service.gradient_start}, ${service.gradient_end})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <Typography sx={{ color: "white", fontSize: 24 }}>
                    {service.icon_name.charAt(0)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {service.title}
                  </Typography>
                  <Chip
                    label={
                      service.is_active
                        ? t("admin.contentManagement.active")
                        : t("admin.contentManagement.inactive")
                    }
                    color={service.is_active ? "success" : "default"}
                    size="small"
                  />
                </Box>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {service.description}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                <Tooltip title={t("admin.contentManagement.edit")}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(service)}
                    sx={{ color: "#00d4ff" }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("admin.contentManagement.delete")}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(service.id!)}
                    sx={{ color: "#ff3366" }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ServicesTab;